import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import useDocumentTitle from '../../utils/useDocumentTitle';
import Card from '../../components/Card';
import Select from '../../components/Select';
import CurrentUser from '../../models/CurrentUser';
import {
  ADMIN_UPDATE_MESSAGE_TEMPLATE,
  ADMIN_RESYNC_MESSAGE_TEMPLATE,
  ADMIN_UPDATE_GROUP,
} from '../../common/permissions';
import { USERS_GROUP_TYPE__ORGANIZATION } from '../../common/constants';
import {
  apiAdminAllUsersGroups,
  apiAdminResyncAllMessageTemplates,
  apiAdminOrganisationStaffLanguageCounts,
} from '../../common/api/admin';
import { callMethod } from '../../common/utilsClient/ddp/actions';
import { default as UsersGroupSelect } from '../../common/selectors/UsersGroup';
import MessageTemplatesAccordion from './components/MessageTemplatesAccordion';
import OrganisationLanguages from './components/OrganisationLanguages';
import ResyncAllControl from './components/ResyncAllControl';
import { getOrganisationId, setOrganisationId } from './store';
import useRefreshCounts from './useRefreshCounts';

const SettingsEmailTemplates = () => {
  const { t } = useTranslation();
  useDocumentTitle([t('settings'), t('messageTemplates')]);

  const canUpdate = !!useSelector(
    CurrentUser.select.getPermissionGrant(ADMIN_UPDATE_MESSAGE_TEMPLATE),
  );
  // TECH-2730. No canCreate. The create path was not merely unused, it was
  // UNREACHABLE: `isDefault` is never set now that the publication stopped
  // synthesising default rows, so the editor's `isUpdate` was always true and
  // apiAdminCreateOrganisationMessageTemplate was never called from this
  // screen. Every organisation is seeded with all nine messages, so the only
  // thing a create could produce is a second row for a purpose that already
  // has one — which makes delivered content arbitrary.

  const canUpdateGroup = !!useSelector(
    CurrentUser.select.getPermissionGrant(ADMIN_UPDATE_GROUP),
  );
  // TECH-2730. The Resync button: its own permission, because it reads every
  // patient record once per organisation. See ResyncAllControl.
  const canResync = !!useSelector(
    CurrentUser.select.getPermissionGrant(ADMIN_RESYNC_MESSAGE_TEMPLATE),
  );

  const { ready: isGroupsReady } = useDDPSubscription(
    apiAdminAllUsersGroups.withParams(),
  );
  const allGroups = useSelector(UsersGroupSelect.all());
  const organisationOptions = useMemo(
    () =>
      (allGroups || [])
        .filter((group) => group.type === USERS_GROUP_TYPE__ORGANIZATION)
        .map((group) => ({
          value: group._id,
          label: group.getName(),
        })),
    [allGroups],
  );

  const dispatch = useDispatch();
  const organisationId = useSelector(getOrganisationId);

  const organisation = useMemo(
    () => (allGroups || []).find((group) => group._id === organisationId),
    [allGroups, organisationId],
  );

  // TECH-2730. The completeness denominator. Comes from the organisation
  // itself, not from the provider portal's interface locales: those are a
  // build-time environment variable CI derives from a readdir of
  // public/locales, which is why a customer could only ever write in the
  // languages the STAFF interface happened to have been translated into.
  const configuredLanguages = useMemo(
    () => (organisation && organisation.messageLanguages) || [],
    [organisation],
  );

  // TECH-2730. THE COUNTS ARE A SNAPSHOT ON THE ORGANISATION, NOT A CALL.
  //
  // Until 2026-09-23 this screen called api.admin.organisationPatientLanguages
  // from an effect keyed on organisationId — on every open and on every
  // change of the dropdown above — and that method ran a Recipients aggregate
  // each time. On dev3 four such calls in one day took 149s, 158s, 114s and
  // 125s: 546 seconds, the most expensive method on the server, from a page
  // being opened. With MONGO_URL on readPreference=secondary that landed on
  // the secondaries, which sat at full CPU and read from disk for hours after
  // each one.
  //
  // The numbers now live on the organisation document the page already
  // subscribes to (patientLanguageCounts, patientLanguageCountsAt), written by
  // api.admin.resyncOrganisationLanguages when an administrator clicks
  // Refresh and once when an organisation is created. Opening the page and
  // switching organisation read a document; they run nothing. A number an
  // administrator asked for a minute ago beats a live number that costs two
  // minutes of database CPU — and the panel shows WHEN it was taken.
  const patientLanguageCounts = organisation
    ? organisation.patientLanguageCounts
    : undefined;
  const patientLanguageCountsAt = organisation
    ? organisation.patientLanguageCountsAt
    : undefined;

  // Refresh state is keyed to the organisation it was issued for — see the
  // hook for the race the screen-level version had.
  const {
    refreshingCounts,
    refreshCountsFailed,
    refreshAlreadyRunning,
    refresh: handleRefreshCounts,
  } = useRefreshCounts(organisationId, patientLanguageCountsAt);

  // TECH-2730 #4. Staff counts per notification language, for the Staff
  // header. Live, not a snapshot: staff are few, so the count is cheap. The
  // answer is kept with the organisation it was asked for, so a slow reply
  // never lands on the next organisation picked.
  const [staffCounts, setStaffCounts] = useState(null);
  useEffect(() => {
    if (!organisationId) {
      return undefined;
    }
    let current = true;
    dispatch(
      callMethod(apiAdminOrganisationStaffLanguageCounts, { organisationId }),
    )
      .then((result) => {
        if (current) {
          setStaffCounts({ organisationId, counts: result.counts });
        }
        return null;
      })
      // No counts is the honest fallback; the header simply shows none.
      .catch(() => null);
    return () => {
      current = false;
    };
  }, [dispatch, organisationId]);
  const staffLanguageCounts =
    staffCounts && staffCounts.organisationId === organisationId
      ? staffCounts.counts
      : null;

  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [resyncFailed, setResyncFailed] = useState(false);
  const [resyncAlreadyRunning, setResyncAlreadyRunning] = useState(false);
  const handleResyncAll = useCallback(() => {
    setResyncing(true);
    setResyncResult(null);
    setResyncFailed(false);
    setResyncAlreadyRunning(false);
    dispatch(callMethod(apiAdminResyncAllMessageTemplates, {}))
      .then((result) => {
        setResyncing(false);
        setResyncResult(result);
        return null;
      })
      .catch((error) => {
        setResyncing(false);
        // The server's own code for "one is already running": the truthful
        // message is that a resync IS happening, not that nothing changed.
        if (
          error &&
          typeof error.error === 'string' &&
          error.error.endsWith('.alreadyRunning')
        ) {
          setResyncAlreadyRunning(true);
        } else {
          setResyncFailed(true);
        }
      });
  }, [dispatch]);

  const handleSelectOrganisation = useCallback(
    (value) => {
      dispatch(setOrganisationId(value));
    },
    [dispatch],
  );

  return (
    <>
      <Card>
        <div className="stack-4" data-testid="message-templates-table">
          <div className="cluster-4 items-end justify-between">
            <div className="w-72">
              <Select
                data-testid="select-organisation"
                options={organisationOptions}
                value={organisationId}
                onChange={handleSelectOrganisation}
                placeholder={t('groups')}
                isDisabled={!isGroupsReady}
              />
            </div>
            {/* TECH-2730. No "Add" control, deliberately. These nine messages
                (two staff, seven PEP — see SYSTEM_MESSAGE_PURPOSES; this said
                "six" until phase 2 added the three OTP codes)
                are a fixed, standard set and every organisation is seeded with
                all of them, so the only thing Add could create is a SECOND row
                for a purpose that already has one. Resolution takes the first
                match per purpose, so a duplicate makes what a patient receives
                ambiguous. Templates are edited here, never created. */}
            {canResync && (
              <ResyncAllControl
                onConfirm={handleResyncAll}
                resyncing={resyncing}
                result={resyncResult}
                failed={resyncFailed}
                alreadyRunning={resyncAlreadyRunning}
              />
            )}
          </div>
          <OrganisationLanguages
            organisationId={organisationId}
            configuredLanguages={configuredLanguages}
            patientLanguageCounts={patientLanguageCounts}
            patientLanguageCountsAt={patientLanguageCountsAt}
            onRefreshCounts={handleRefreshCounts}
            refreshingCounts={refreshingCounts}
            refreshCountsFailed={refreshCountsFailed}
            refreshAlreadyRunning={refreshAlreadyRunning}
            canUpdate={canUpdateGroup}
          />
          <MessageTemplatesAccordion
            organisationId={organisationId}
            configuredLanguages={configuredLanguages}
            canUpdate={canUpdate}
            patientLanguageCounts={patientLanguageCounts}
            patientLanguageCountsAt={patientLanguageCountsAt}
            staffLanguageCounts={staffLanguageCounts}
          />
        </div>
      </Card>
    </>
  );
};

export default SettingsEmailTemplates;
